/**
 * LocationsImport — bulk creation, option 1d ("one canvas").
 *
 * Built 2026-08-07 (Nick) from `Locations - Admin & Bulk Creation.dc.html`.
 * One surface serves every route: upload a file, paste a block out of a
 * spreadsheet, type blank rows by hand, or clone an existing location N times —
 * all of them land as rows in the same grid, and fixing a bad cell is typing in
 * it. No wizard steps, no mode.
 *
 * Validation is the point of the screen, and it is NOT client-side theatre:
 * every keystroke settles into a real `validateLocationBatch` dry run through
 * the typed client, so the "will create / will update / rejected" counts are
 * the server's verdict and the commit cannot contradict the preview. Each row
 * is validated against ITS OWN country's rules (a UK postcode and a Japanese
 * one are both right, in different rows of the same file).
 *
 * Commit goes through `bulkCreateLocations`, awaited, with rejected rows coming
 * back as a downloadable CSV — the operator's chosen error policy.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Btn, Icon, T } from "@v0y4ge/jrni-ui";
import { validateLocationBatch, bulkCreateLocations } from "@v0y4ge/api-client";
import type { LocationBatchIssue } from "@v0y4ge/api-contracts";
import { LOCATIONS_OPTS } from "../../shell/request-opts";
import { Screen, PageHeader } from "../../shell/chrome";
import { useIsMobile } from "../../shell/useResponsive";
import { can } from "../../shell/access";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { logAction } from "../../lib/audit";
import { useLocations } from "./useLocations";
import { useT, t as i18nT } from "../../lib/i18n";
import {
  parseDelimited, autoMapColumns, IMPORT_FIELDS, templateCsv, downloadText,
  type LocField,
} from "./location-validation";

type Draft = Record<string, string | number | boolean | undefined>;

interface Verdict {
  total: number;
  createCount: number;
  updateCount: number;
  rejectCount: number;
  issues: LocationBatchIssue[];
}

const COL_W = 168;
const SEV_TONE: Record<string, { hue: string; bg: string; br: string; fg: string }> = {
  error: { hue: "#B0093A", bg: "#fff", br: "#F5C9D5", fg: "#B0093A" },
  warning: { hue: "#8A6300", bg: "#FFF6E5", br: "#F2E6C7", fg: "#8A6300" },
  info: { hue: T.p1, bg: T.p4, br: "#D5C6FA", fg: T.p1 },
};

export default function LocationsImport() {
  const t = useT();
  const nav = useNav();
  const canWrite = can("locations.write");
  const { locations, reload } = useLocations();

  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<(LocField | null)[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [sourceName, setSourceName] = useState<string>("");
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [checking, setChecking] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [cloneId, setCloneId] = useState("");
  const [cloneN, setCloneN] = useState(3);
  const fileRef = useRef<HTMLInputElement>(null);
  /* Below ~1100px a hard-300px issues panel starved the grid to 334px of its
     1552px width — 7 of 9 columns, and the per-cell error treatment the whole
     "fix it in place" design depends on, sat off-screen (verifier, 2026-08-07).
     The panel leaves the row entirely and becomes a strip above the grid. */
  const narrow = useIsMobile(1100);

  /* rows + mapping → the contract's LocationBatchRow shape. */
  const drafts: Draft[] = useMemo(() => rows.map((r, i) => {
    const d: Draft = { rowNumber: i + 1 };
    mapping.forEach((field, ci) => {
      if (!field) return;
      const v = (r[ci] ?? "").trim();
      if (v) d[field] = v;
    });
    return d;
  }), [rows, mapping]);

  /* Debounced dry run — the server is the authority on the counts. */
  const runCheck = useCallback(async (input: Draft[]) => {
    if (!input.length) { setVerdict(null); return; }
    setChecking(true);
    try {
      const res = await (validateLocationBatch as unknown as (b: unknown, o: unknown) => Promise<Verdict>)(
        { rows: input, autoFix: false }, LOCATIONS_OPTS,
      );
      setVerdict(res);
    } catch (e) {
      setVerdict(null);
      toast({
        tone: "danger",
        title: t("locations.import.couldnTCheckTheRows", "Couldn't check the rows"),
        subtitle: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setChecking(false);
    }
  }, [t]);

  useEffect(() => {
    if (!drafts.length) { setVerdict(null); return; }
    const h = setTimeout(() => { void runCheck(drafts); }, 350);
    return () => clearTimeout(h);
  }, [drafts, runCheck]);

  /* issue lookup: "row:field" → the worst issue on that cell. */
  const cellIssues = useMemo(() => {
    const m = new Map<string, LocationBatchIssue>();
    for (const i of verdict?.issues ?? []) {
      const key = `${i.rowNumber}:${i.field}`;
      const prev = m.get(key);
      if (!prev || (prev.severity !== "error" && i.severity === "error")) m.set(key, i);
    }
    return m;
  }, [verdict]);

  const rowHasError = useMemo(() => {
    const s = new Set<number>();
    for (const i of verdict?.issues ?? []) if (i.severity === "error") s.add(i.rowNumber);
    return s;
  }, [verdict]);

  /** Issues grouped by (code + field), newest count first — the to-do list. */
  const grouped = useMemo(() => {
    const g = new Map<string, { code: string; field: string; severity: string; message: string; rows: number[]; fixable: number }>();
    for (const i of verdict?.issues ?? []) {
      const key = `${i.code}:${i.field}`;
      const hit = g.get(key);
      if (hit) { hit.rows.push(i.rowNumber); if (i.suggestion) hit.fixable++; }
      else g.set(key, { code: i.code, field: i.field, severity: i.severity, message: i.message, rows: [i.rowNumber], fixable: i.suggestion ? 1 : 0 });
    }
    return [...g.values()].sort((a, b) => b.rows.length - a.rows.length);
  }, [verdict]);

  const loadTable = (headerRow: string[], dataRows: string[][], name: string) => {
    setHeaders(headerRow);
    setMapping(autoMapColumns(headerRow));
    setRows(dataRows);
    setSourceName(name);
    setIssuesOnly(false);
  };

  const onFile = (file: File) => {
    if (/\.(xlsx|xls)$/i.test(file.name)) {
      toast({
        tone: "warn",
        title: t("locations.import.saveItAsCsvFirst", "Save it as CSV first"),
        subtitle: t("locations.import.spreadsheetBinariesArenTReadHere", "Spreadsheet binaries aren't read here yet — export the sheet as CSV, or paste the rows straight into the grid."),
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const { headers: h, rows: r } = parseDelimited(String(reader.result ?? ""));
      if (!h.length) {
        toast({ tone: "danger", title: t("locations.import.thatFileHadNoRows", "That file had no rows"), subtitle: file.name });
        return;
      }
      loadTable(h, r, file.name);
    };
    reader.readAsText(file);
  };

  const addBlankRows = (n: number) => {
    const h = headers.length ? headers : IMPORT_FIELDS.map((f) => f.label);
    const m = mapping.length ? mapping : IMPORT_FIELDS.map((f) => f.key);
    setHeaders(h);
    setMapping(m);
    setRows((r) => [...r, ...Array.from({ length: n }, () => h.map(() => ""))]);
    if (!sourceName) setSourceName(i18nT("locations.import.typedByHand", "Typed by hand"));
  };

  const applyPaste = () => {
    const { headers: h, rows: r } = parseDelimited(pasteText);
    if (!h.length) {
      toast({ tone: "danger", title: t("locations.import.nothingToPaste", "Nothing to paste"), subtitle: t("locations.import.copyTheHeaderRowTooSoTheColumns", "Copy the header row too, so the columns can be matched.") });
      return;
    }
    loadTable(h, r, i18nT("locations.import.pastedRows", "Pasted rows"));
    setPasteOpen(false);
    setPasteText("");
  };

  const applyClone = () => {
    const src = locations.find((l) => (l.raw?.id ?? l.id) === cloneId);
    if (!src) return;
    const raw = (src.raw ?? {}) as Record<string, unknown>;
    const h = IMPORT_FIELDS.map((f) => f.label);
    const m = IMPORT_FIELDS.map((f) => f.key);
    const made = Array.from({ length: Math.max(1, Math.min(50, cloneN)) }, (_, i) =>
      IMPORT_FIELDS.map((f) => {
        if (f.key === "name") return `${String(raw.name ?? src.name)} ${i + 2}`;
        if (f.key === "externalCode") return ""; // a store code is unique by definition
        return String(raw[f.key] ?? "");
      }));
    setHeaders(h);
    setMapping(m);
    setRows((r) => (headers.length ? [...r, ...made] : made));
    setSourceName(i18nT("locations.import.clonedRows", "Cloned rows"));
  };

  /** Apply every server-proposed suggestion (the "Fix all" affordance). */
  const fixAll = async () => {
    setChecking(true);
    try {
      const res = await (validateLocationBatch as unknown as (b: unknown, o: unknown) => Promise<Verdict & { rows?: Draft[] }>)(
        { rows: drafts, autoFix: true }, LOCATIONS_OPTS,
      );
      const fixed = res.rows ?? [];
      setRows((prev) => prev.map((r, ri) => {
        const f = fixed[ri];
        if (!f) return r;
        return r.map((cell, ci) => {
          const field = mapping[ci];
          if (!field) return cell;
          const v = f[field];
          return v === undefined || v === null ? cell : String(v);
        });
      }));
      toast({ tone: "success", title: t("locations.import.suggestionsApplied", "Suggestions applied"), subtitle: t("locations.import.reCheckingTheRows", "Re-checking the rows…") });
    } catch (e) {
      toast({ tone: "danger", title: t("locations.import.couldnTApplyTheFixes", "Couldn't apply the fixes"), subtitle: e instanceof Error ? e.message : String(e) });
    } finally {
      setChecking(false);
    }
  };

  const buildRejectsCsv = () => {
    const byRow = new Map<number, string[]>();
    for (const i of verdict?.issues ?? []) {
      if (i.severity !== "error") continue;
      const list = byRow.get(i.rowNumber) ?? [];
      list.push(`${i.field}: ${i.message}`);
      byRow.set(i.rowNumber, list);
    }
    const esc = (s: string) => (/[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
    const cols = IMPORT_FIELDS.map((f) => f.key);
    const lines = [["rowNumber", ...IMPORT_FIELDS.map((f) => f.label), "_error"].join(",")];
    drafts.forEach((d) => {
      const errs = byRow.get(Number(d.rowNumber));
      if (!errs) return;
      lines.push([String(d.rowNumber), ...cols.map((c) => esc(String(d[c] ?? ""))), esc(errs.join(" · "))].join(","));
    });
    return lines.join("\r\n");
  };

  const commit = async () => {
    if (committing || !verdict) return;
    setCommitting(true);
    try {
      const res = await (bulkCreateLocations as unknown as (b: unknown, o: unknown) => Promise<{
        createdCount: number; updatedCount: number; rejectedCount: number; rejectsCsv?: string | null;
      }>)({ rows: drafts, onDuplicateExternalCode: "update" }, LOCATIONS_OPTS);
      logAction("location", "bulk", `${res.createdCount} created, ${res.updatedCount} updated`, "created");
      reload();
      if (res.rejectsCsv) downloadText("locations-rejects.csv", res.rejectsCsv);
      toast({
        tone: res.rejectedCount ? "warn" : "success",
        title: `${res.createdCount + res.updatedCount} ${t("locations.import.locationsAreLive", "locations are live")}`,
        subtitle: `${res.createdCount} ${t("locations.import.created", "created")} · ${res.updatedCount} ${t("locations.import.updated", "updated")}${res.rejectedCount ? ` · ${res.rejectedCount} ${t("locations.import.rejectedDownloaded", "rejected (file downloaded)")}` : ""}`,
      });
      setCommitting(false);
      nav.go("locations");
    } catch (e) {
      setCommitting(false);
      toast({
        tone: "danger",
        title: t("locations.import.importFailed", "Import failed"),
        subtitle: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const visibleRows = rows
    .map((r, i) => ({ r, n: i + 1 }))
    .filter(({ n }) => !issuesOnly || (verdict?.issues ?? []).some((x) => x.rowNumber === n));

  const chip = (label: string, icon: React.ReactNode, onClick: () => void, on?: boolean) => (
    <button key={label} type="button" onClick={onClick} disabled={!canWrite}
      style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 32, padding: "0 12px", borderRadius: 16, background: on ? T.p4 : T.white, border: `1px solid ${on ? "#D5C6FA" : T.n4}`, color: on ? T.p1 : T.n2, fontSize: 12.5, fontWeight: 500, cursor: canWrite ? "pointer" : "default", fontFamily: T.font, whiteSpace: "nowrap" }}>
      {icon}{label}
    </button>
  );

  const canCommit = !!verdict && verdict.total > 0 && (verdict.createCount + verdict.updateCount) > 0 && !committing && canWrite;

  return (
    <Screen rail="locations">
      <PageHeader
        title={t("locations.import.addLocationsInBulk", "Add locations in bulk")}
        subtitle={t("locations.import.uploadPasteTypeOrClone", "Upload a file, paste from a spreadsheet, type rows by hand, or clone an existing location. Every row is checked against its own country's rules.")}
        breadcrumb={
          <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("locations")}>
            <Icon.arrow dir="left" size={11} /> {t("locations.editorPage.locations", "Locations")}
          </span>
        }
        actions={
          <>
            <Btn variant="secondary" icon={<Icon.download size={14} />} onClick={() => downloadText("locations-template.csv", templateCsv())}>
              {t("locations.import.template", "Template")}
            </Btn>
            {verdict?.rejectCount ? (
              <Btn variant="secondary" icon={<Icon.download size={14} />} onClick={() => downloadText("locations-rejects.csv", buildRejectsCsv())}>
                {t("locations.import.downloadRejects", "Download rejects")} ({verdict.rejectCount})
              </Btn>
            ) : null}
            <Btn icon={<Icon.check size={14} />} disabled={!canCommit} onClick={() => void commit()}>
              {committing
                ? t("locations.import.creating", "Creating…")
                : `${t("locations.import.create", "Create")} ${verdict ? verdict.createCount + verdict.updateCount : 0}`}
            </Btn>
          </>
        }
      />

      {/* Source strip */}
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 8, padding: "11px 24px", borderBottom: `1px solid ${T.border}`, background: T.white, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: T.soft, marginRight: 2 }}>
          {t("locations.import.addRowsFrom", "Add rows from")}
        </span>
        {chip(t("locations.import.upload", "Upload"), <Icon.upload size={13} />, () => fileRef.current?.click(), !!sourceName && /\.csv|\.tsv|\.txt/i.test(sourceName))}
        {chip(t("locations.import.paste", "Paste"), <Icon.copy size={13} />, () => setPasteOpen((v) => !v), pasteOpen)}
        {chip(t("locations.import.blankRows", "Blank rows"), <Icon.plus size={13} />, () => addBlankRows(5))}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <select value={cloneId} onChange={(e) => setCloneId(e.target.value)} disabled={!canWrite}
            style={{ height: 32, borderRadius: 16, border: `1px solid ${T.n4}`, background: T.white, color: cloneId ? T.n1 : T.n3, fontSize: 12.5, fontFamily: T.font, padding: "0 10px", maxWidth: 190 }}>
            <option value="">{i18nT("locations.import.cloneAnExistingLocation", "Clone an existing location…")}</option>
            {locations.map((l) => <option key={l.id} value={String(l.raw?.id ?? l.id)}>{l.name}</option>)}
          </select>
          <input type="number" min={1} max={50} value={cloneN} disabled={!canWrite}
            onChange={(e) => setCloneN(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            style={{ width: 56, height: 32, borderRadius: 16, border: `1px solid ${T.n4}`, background: T.white, color: T.n1, fontSize: 12.5, fontFamily: T.font, padding: "0 10px" }} />
          {chip(t("locations.import.clone", "Clone"), <Icon.copy size={13} />, applyClone)}
        </span>
        <span style={{ flex: 1 }} />
        {sourceName ? (
          <span style={{ fontSize: 12.5, color: T.soft }}>
            {rows.length} {rows.length === 1 ? t("locations.import.row", "row") : t("locations.import.rows", "rows")} · <span style={{ fontFamily: T.fontMono, fontSize: 12, color: T.n2 }}>{sourceName}</span>
          </span>
        ) : null}
        <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
      </div>

      {pasteOpen ? (
        <div style={{ flexShrink: 0, padding: "12px 24px", borderBottom: `1px solid ${T.border}`, background: T.canvas }}>
          <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)}
            placeholder={i18nT("locations.import.pastePlaceholder", "Paste rows here, header row included — tab- or comma-separated.")}
            style={{ width: "100%", height: 110, borderRadius: 8, border: `1px solid ${T.n4}`, padding: 10, fontSize: 12.5, fontFamily: T.fontMono, resize: "vertical", outline: "none", boxSizing: "border-box" }} />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Btn size="sm" onClick={applyPaste}>{t("locations.import.loadRows", "Load rows")}</Btn>
            <Btn size="sm" variant="secondary" onClick={() => { setPasteOpen(false); setPasteText(""); }}>{t("common.actions.cancel", "Cancel")}</Btn>
          </div>
        </div>
      ) : null}

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: narrow ? "column" : "row", gap: 14, padding: "14px 24px 20px", boxSizing: "border-box", overflowY: narrow ? "auto" : "visible" }}>
        {!rows.length ? (
          <div onClick={() => canWrite && fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
            style={{ flex: 1, border: `2px dashed ${T.n4}`, borderRadius: 12, background: T.white, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, cursor: canWrite ? "pointer" : "default" }}>
            <span style={{ width: 52, height: 52, borderRadius: 14, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.upload size={23} /></span>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.n1 }}>{t("locations.import.dropACsvHere", "Drop a CSV here")}</div>
            <div style={{ fontSize: 13, color: T.soft }}>{t("locations.import.orPasteBlankClone", "…or paste from a spreadsheet, add blank rows, or clone an existing location.")}</div>
          </div>
        ) : (
          <>
            {/* Grid. Stacked (narrow) it must NOT flex for vertical space — the
                panel above it would squeeze it to nothing on a short viewport;
                it takes a real minimum height and the body scrolls instead. */}
            <div style={{ flex: narrow ? "0 0 auto" : 1, minWidth: 0, minHeight: narrow ? 340 : 0, background: T.white, borderRadius: 10, border: `1px solid ${T.border}`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderBottom: `1px solid ${T.border}` }}>
                <button type="button" onClick={() => setIssuesOnly((v) => !v)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 30, padding: "0 11px", borderRadius: 15, background: issuesOnly ? T.p4 : T.white, border: `1px solid ${issuesOnly ? "#D5C6FA" : T.n4}`, color: issuesOnly ? T.p1 : T.n2, fontSize: 12.5, fontWeight: 500, cursor: "pointer", fontFamily: T.font }}>
                  <span style={{ width: 26, height: 15, borderRadius: 8, background: issuesOnly ? T.p1 : T.n4, position: "relative", flexShrink: 0 }}>
                    <span style={{ position: "absolute", top: 2, left: issuesOnly ? 13 : 2, width: 11, height: 11, borderRadius: "50%", background: "#fff", transition: "left 120ms" }} />
                  </span>
                  {t("locations.import.onlyRowsWithIssues", "Only rows with issues")}
                </button>
                <span style={{ fontSize: 12.5, color: T.soft }}>
                  {t("locations.import.showing", "Showing")} {visibleRows.length} {t("locations.import.of", "of")} {rows.length}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 12, color: T.soft }}>
                  {checking ? t("locations.import.checking", "Checking…") : t("locations.import.everyHeaderIsADropdown", "Every header is a dropdown — remap a column without leaving the grid")}
                </span>
              </div>

              <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <div style={{ minWidth: 40 + headers.length * COL_W }}>
                  {/* header */}
                  <div style={{ display: "flex", position: "sticky", top: 0, zIndex: 2, background: T.n6, borderBottom: `1px solid ${T.border}` }}>
                    <span style={{ width: 40, flexShrink: 0 }} />
                    {headers.map((h, ci) => (
                      <span key={ci} style={{ width: COL_W, flexShrink: 0, padding: "6px 8px", boxSizing: "border-box" }}>
                        <span style={{ display: "block", fontSize: 10.5, color: T.n3, fontFamily: T.fontMono, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h || "—"}</span>
                        <select value={mapping[ci] ?? ""} disabled={!canWrite}
                          onChange={(e) => setMapping((m) => m.map((x, xi) => (xi === ci ? ((e.target.value || null) as LocField | null) : x)))}
                          style={{ width: "100%", height: 26, marginTop: 2, borderRadius: 5, border: `1px solid ${mapping[ci] ? T.n4 : "#E0E2E8"}`, background: mapping[ci] ? T.white : T.canvas, color: mapping[ci] ? T.n1 : T.n3, fontSize: 12, fontWeight: 600, fontFamily: T.font, padding: "0 4px" }}>
                          <option value="">{i18nT("locations.import.donTImport", "Don't import")}</option>
                          {IMPORT_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </select>
                      </span>
                    ))}
                  </div>

                  {visibleRows.map(({ r, n }) => (
                    <div key={n} style={{ display: "flex", borderBottom: `1px solid ${T.n6}` }}>
                      <span style={{ width: 40, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontFamily: T.fontMono, color: rowHasError.has(n) ? "#B0093A" : T.n3, background: rowHasError.has(n) ? "#FDF2F5" : T.canvas }}>{n}</span>
                      {headers.map((_, ci) => {
                        const field = mapping[ci];
                        const issue = field ? cellIssues.get(`${n}:${field}`) : undefined;
                        const bad = issue?.severity === "error";
                        const warn = issue?.severity === "warning";
                        return (
                          <span key={ci} style={{ width: COL_W, flexShrink: 0, boxSizing: "border-box", padding: 0, position: "relative" }}>
                            <input
                              value={r[ci] ?? ""} readOnly={!canWrite} title={issue?.message ?? ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                setRows((prev) => prev.map((row, ri) => (ri === n - 1 ? row.map((c, cj) => (cj === ci ? v : c)) : row)));
                              }}
                              style={{
                                width: "100%", height: 36, border: "none", outline: "none", boxSizing: "border-box",
                                padding: "0 8px", fontSize: 12.5, fontFamily: field && ["postalCode", "country", "timezone", "externalCode", "phone"].includes(field) ? T.fontMono : T.font,
                                color: bad ? "#B0093A" : field ? T.n1 : T.n3,
                                background: bad ? "#FDECEF" : warn ? "#FFFBF0" : "transparent",
                                boxShadow: bad ? "inset 0 -2px 0 #F80449" : warn ? "inset 0 -2px 0 #E4B305" : "none",
                              }}
                            />
                          </span>
                        );
                      })}
                    </div>
                  ))}

                  <button type="button" onClick={() => addBlankRows(1)} disabled={!canWrite}
                    style={{ display: "flex", alignItems: "center", gap: 8, height: 38, padding: "0 12px", border: "none", background: "transparent", color: T.p1, fontSize: 12.5, fontWeight: 500, cursor: "pointer", fontFamily: T.font }}>
                    <Icon.plus size={13} /> {t("locations.import.addABlankRow", "Add a blank row")}
                  </button>
                </div>
              </div>
            </div>

            {/* Issues panel — a right-hand column on wide viewports, a strip
                above the grid when narrow (order:-1), so the grid always owns
                the full width of the row. */}
            <div style={{ width: narrow ? "100%" : 300, flexShrink: 0, order: narrow ? -1 : 0, maxHeight: narrow ? 188 : undefined, background: T.white, borderRadius: 10, border: `1px solid ${T.border}`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ flexShrink: 0, padding: "12px 14px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("locations.import.issues", "Issues")}</span>
                <span style={{ fontSize: 12, fontFamily: T.fontMono, color: T.soft }}>{verdict?.issues.length ?? 0}</span>
                <span style={{ flex: 1 }} />
                {grouped.some((g) => g.fixable) ? (
                  <button type="button" onClick={() => void fixAll()} disabled={checking}
                    style={{ border: "none", background: "transparent", color: T.p1, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font, padding: 0 }}>
                    {t("locations.import.fixAll", "Fix all")}
                  </button>
                ) : null}
              </div>

              <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {!grouped.length ? (
                  <div style={{ padding: "26px 16px", textAlign: "center", color: T.soft, fontSize: 13 }}>
                    {checking ? t("locations.import.checking", "Checking…") : t("locations.import.everyRowIsClean", "Every row is clean.")}
                  </div>
                ) : grouped.map((g) => {
                  const tone = SEV_TONE[g.severity] ?? SEV_TONE.info;
                  return (
                    <div key={`${g.code}:${g.field}`} style={{ padding: "12px 14px", borderBottom: `1px solid ${T.n6}` }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: tone.hue, fontVariantNumeric: "tabular-nums" }}>{g.rows.length}</span>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: T.n1, lineHeight: 1.35 }}>{g.message}</span>
                      </div>
                      <div style={{ marginTop: 5, fontSize: 11.5, color: T.soft, lineHeight: 1.45 }}>
                        {t("locations.import.rowsColon", "Rows")} {g.rows.slice(0, 8).join(", ")}{g.rows.length > 8 ? ` +${g.rows.length - 8}` : ""}
                      </div>
                      {g.fixable ? (
                        <button type="button" onClick={() => void fixAll()} disabled={checking}
                          style={{ marginTop: 8, display: "inline-flex", alignItems: "center", height: 28, padding: "0 11px", borderRadius: 6, background: tone.bg, border: `1px solid ${tone.br}`, color: tone.fg, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: T.font }}>
                          {t("locations.import.applySuggestion", "Apply suggestion")} ({g.fixable})
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div style={{ flexShrink: 0, padding: "12px 14px", borderTop: `1px solid ${T.border}`, background: T.canvas }}>
                {([
                  { n: verdict?.createCount ?? 0, label: t("locations.import.willBeCreated", "will be created"), hue: "#0A8375" },
                  { n: verdict?.updateCount ?? 0, label: t("locations.import.willUpdateAnExistingStoreCode", "will update an existing store code"), hue: T.p1 },
                  { n: verdict?.rejectCount ?? 0, label: t("locations.import.goToTheRejectsFile", "go to the rejects file"), hue: "#B0093A" },
                ]).map((s) => (
                  <div key={s.label} style={{ display: "flex", alignItems: "baseline", gap: 7, marginBottom: 4 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: s.hue, fontVariantNumeric: "tabular-nums" }}>{s.n.toLocaleString()}</span>
                    <span style={{ fontSize: 12, color: T.soft, lineHeight: 1.3 }}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Screen>
  );
}
